const LAST_VISITED_PAGE_PREFIX = 'adminPortal:lastVisitedPage'
const LAST_ACTIVE_COMPANY_PREFIX = 'adminPortal:lastActiveCompany'

type LastActiveCompany = {
  companyId: string
  storeName: string
}

const excludedPaths = new Set(['/', '/auth-error', '/get-started'])

const excludedPathPrefixes = ['/public', '/_auth']

const isTrackablePath = (pathname: string) => {
  if (excludedPaths.has(pathname)) return false

  return !excludedPathPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

const getStorageKey = (userEmail: string, companyId: string) =>
  `${LAST_VISITED_PAGE_PREFIX}:${encodeURIComponent(userEmail.trim().toLowerCase())}:${encodeURIComponent(companyId)}`

const getLastActiveCompanyStorageKey = (userEmail: string) =>
  `${LAST_ACTIVE_COMPANY_PREFIX}:${encodeURIComponent(userEmail.trim().toLowerCase())}`

const normalizeStoredPath = (storedPath: string, storeName: string) => {
  try {
    const url = new URL(storedPath, window.location.origin)

    if (url.origin !== window.location.origin || !isTrackablePath(url.pathname)) {
      return null
    }

    if (url.searchParams.has('code') || url.searchParams.has('state')) {
      return null
    }

    url.searchParams.set('storeName', storeName)
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export const saveLastVisitedPage = (
  userEmail: string | undefined,
  companyId: string | null | undefined,
  storeName: string | undefined,
  currentPath: string
) => {
  if (!userEmail || !companyId || !storeName) return

  const normalizedPath = normalizeStoredPath(currentPath, storeName)
  if (!normalizedPath) return

  try {
    localStorage.setItem(getStorageKey(userEmail, companyId), normalizedPath)
    localStorage.setItem(
      getLastActiveCompanyStorageKey(userEmail),
      JSON.stringify({ companyId, storeName } satisfies LastActiveCompany)
    )
  } catch {
    // Keep navigation working when storage is unavailable.
  }
}

export const getLastActiveCompany = (userEmail: string | undefined): LastActiveCompany | null => {
  if (!userEmail) return null

  try {
    const storedCompany = localStorage.getItem(getLastActiveCompanyStorageKey(userEmail))
    if (!storedCompany) return null

    const company = JSON.parse(storedCompany) as Partial<LastActiveCompany>
    if (typeof company.companyId !== 'string' || typeof company.storeName !== 'string') {
      return null
    }

    return {
      companyId: company.companyId,
      storeName: company.storeName,
    }
  } catch {
    return null
  }
}

export const getLastVisitedPage = (
  userEmail: string | undefined,
  companyId: string | null | undefined,
  storeName: string | undefined
) => {
  if (!userEmail || !companyId || !storeName) return null

  const storageKey = getStorageKey(userEmail, companyId)

  try {
    const storedPath = localStorage.getItem(storageKey)
    if (!storedPath) return null

    const normalizedPath = normalizeStoredPath(storedPath, storeName)
    if (!normalizedPath) {
      localStorage.removeItem(storageKey)
      return null
    }

    return normalizedPath
  } catch {
    return null
  }
}
